import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { formatDay } from "../workReports/format";
import { createWarranty, listWarranties } from "./api";
import { STATE_COLORS, basisLabels, categoryLabels, stateLabels } from "./labels";

/** A warranty for work accepted before the app was used: acceptance date entered by hand. */
const NewWarrantyModal = ({ open, meta, project, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const bases = basisLabels(t);

  useEffect(() => {
    if (open)
      form.setFieldsValue({ project: project || undefined, contractBasis: "vob", title: "" });
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createWarranty({
        title: values.title,
        project: values.project || null,
        contractBasis: values.contractBasis,
        acceptanceDate: values.acceptanceDate.format("YYYY-MM-DD")
      });
      history.push(`/user/warranty/warranties/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="warranty-page"
      open={open}
      title={t("Warranty without acceptance protocol")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Typography.Paragraph type="secondary">
        {t(
          "For work accepted before you used the app. New acceptances create their warranty themselves."
        )}
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="title" label={t("Title")} rules={[{ required: true, whitespace: true }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="project" label={t("Project")}>
          <Select
            showSearch
            allowClear
            disabled={Boolean(project)}
            optionFilterProp="label"
            options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
          />
        </Form.Item>
        <Form.Item name="contractBasis" label={t("Contract basis")} rules={[{ required: true }]}>
          <Select options={Object.entries(bases).map(([value, label]) => ({ value, label }))} />
        </Form.Item>
        <Form.Item
          name="acceptanceDate"
          label={t("Date of the acceptance")}
          rules={[{ required: true }]}
        >
          <DatePicker className="wty-full" format="DD.MM.YYYY" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const WarrantyList = ({ meta, project }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [searchText, setSearchText] = useState("");
  const [state, setState] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const states = stateLabels(t);
  const categories = categoryLabels(t);

  useEffect(() => {
    setLoading(true);
    listWarranties({ searchText, state: state === "all" ? undefined : state, project })
      .then(res => setData(res.data.data))
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  }, [searchText, state, project]);

  const columns = [
    {
      title: t("Warranty"),
      key: "title",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/warranty/warranties/${item._id}`}>
            <Typography.Text strong>{item.title}</Typography.Text>
          </Link>
          <div className="wty-small">
            {item.periods
              .map(p => `${p.label || categories[p.category]}: ${formatDay(p.end) || "—"}`)
              .join(" · ")}
          </div>
        </div>
      )
    },
    {
      title: t("Customer"),
      key: "client",
      mobile: "subtitle",
      render: (_, item) =>
        [item.client?.name, item.project?.name].filter(Boolean).join(" · ") || "—"
    },
    {
      title: t("Accepted on"),
      key: "acceptanceDate",
      width: 120,
      render: (_, item) => formatDay(item.acceptanceDate) || "—"
    },
    {
      title: t("Ends"),
      key: "end",
      width: 120,
      render: (_, item) => formatDay(item.end) || "—"
    },
    {
      title: t("Status"),
      key: "state",
      mobile: "badge",
      width: 170,
      render: (_, item) => (
        <Space size={[4, 4]} wrap>
          <Tag color={STATE_COLORS[item.state]}>{states[item.state]}</Tag>
          {item.openClaims > 0 && (
            <Tag color={item.overdueClaims ? "error" : "warning"}>
              {t("{{n}} open claims", { n: item.openClaims })}
            </Tag>
          )}
        </Space>
      )
    }
  ];

  const table = (
    <ResponsiveTable
      rowKey="_id"
      loading={loading}
      columns={columns}
      dataSource={data}
      mobileAvatar={false}
      onRow={record => ({
        onDoubleClick: () => history.push(`/user/warranty/warranties/${record._id}`)
      })}
      pagination={data.length > 50 ? { pageSize: 50 } : false}
      scroll={{ x: 760 }}
    />
  );

  return (
    <div>
      <PageToolbar
        filters={
          <Space direction="vertical" className="wty-full">
            <SearchBarComp handleSearch={setSearchText} />
            <Segmented
              value={state}
              onChange={setState}
              options={[
                { value: "all", label: t("All") },
                { value: "active", label: states.active },
                { value: "expiring", label: states.expiring },
                { value: "claims", label: t("With open claims") },
                { value: "expired", label: states.expired }
              ]}
            />
          </Space>
        }
        actions={
          meta?.can.create && (
            <Button icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
              {t("Warranty without protocol")}
            </Button>
          )
        }
      />
      {!loading && !data.length ? (
        <Card>
          <Empty description={t("Warranties start with an accepted acceptance protocol.")} />
        </Card>
      ) : isMobile ? (
        table
      ) : (
        <Card styles={{ body: { padding: 0 } }}>{table}</Card>
      )}
      <NewWarrantyModal
        open={newOpen}
        meta={meta}
        project={project}
        onClose={() => setNewOpen(false)}
      />
    </div>
  );
};

export default WarrantyList;
